// src/pages/LoginPage.js
import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiConfig';
import { trackEvent } from '../lib/analytics';
import './AuthPage.css';

function getAllowedReturnUrlOrigins() {
  const origins = new Set();
  const arsheefRaw = (process.env.REACT_APP_ARSHEEF_APP_URL || '').trim();
  if (arsheefRaw) {
    try {
      origins.add(new URL(arsheefRaw).origin);
    } catch (_) {
      /* ignore invalid ARSHEEF_APP_URL */
    }
  }
  (process.env.REACT_APP_ARSHEEF_RETURN_URL_ORIGINS || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((o) => {
      try {
        origins.add(new URL(o).origin);
      } catch (_) {
        origins.add(o);
      }
    });
  return origins;
}

function isSafeReturnUrl(returnUrl) {
  if (!returnUrl || typeof returnUrl !== 'string') return false;
  try {
    const u = new URL(returnUrl, window.location.origin);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
    const host = u.hostname.toLowerCase();
    if (host === 'localhost' || host === '127.0.0.1') return true;
    if (u.origin === window.location.origin) return true;
    if (getAllowedReturnUrlOrigins().has(u.origin)) return true;
    return false;
  } catch {
    return false;
  }
}


function clearLocalAuthFlags() {
  localStorage.removeItem('user');
  localStorage.removeItem('isLoggedIn');
  localStorage.removeItem('ydim_guest_user');
  localStorage.removeItem('guest_usage_count');
}

function otpSubtitle(deliveryHint) {
  if (deliveryHint === 'sms') {
    return 'أدخل رقم الجوال وسنرسل رمز التحقق عبر رسالة نصية';
  }
  if (deliveryHint === 'auto') {
    return 'أدخل رقم الجوال وسنرسل رمز التحقق إلى جوالك أو بريدك المسجل';
  }
  return 'أدخل رقم الجوال وسنرسل رمز التحقق إلى بريدك المسجل';
}

function otpSentHint(deliveryHint) {
  if (deliveryHint === 'sms') {
    return 'إن كان الرقم مسجلاً، تم إرسال رمز التحقق إلى جوالك.';
  }
  if (deliveryHint === 'auto') {
    return 'إن كان الرقم مسجلاً، تم إرسال رمز التحقق.';
  }
  return 'تم إرسال رمز إلى البريد المسجل لهذا الرقم (إن وُجد).';
}

const LoginPage = () => {
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enterGuestMode, login } = useAuth();

  const [loginTab, setLoginTab] = useState('otp');
  const [otpConfig, setOtpConfig] = useState({
    otpLoginEnabled: false,
    passwordLoginEnabled: false,
    provider: 'email',
    deliveryHint: 'email',
  });

  const [formData, setFormData] = useState({
    mobile: '',
    password: '',
  });

  const [otpCode, setOtpCode] = useState('');
  const [challengeId, setChallengeId] = useState(null);
  const [otpSent, setOtpSent] = useState(false);

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(buildApiUrl(API_ENDPOINTS.LOGIN_OTP_CONFIG));
        const data = await res.json();
        if (!cancel && data) {
          const otpEnabled = Boolean(data.otpLoginEnabled);
          const passwordEnabled = Boolean(data.passwordLoginEnabled);
          setOtpConfig({
            otpLoginEnabled: otpEnabled,
            passwordLoginEnabled: passwordEnabled,
            provider: data.provider || 'email',
            deliveryHint: data.deliveryHint || 'email',
          });
          if (!otpEnabled && passwordEnabled) {
            setLoginTab('password');
          } else {
            setLoginTab('otp');
          }
        }
      } catch {
        /* keep default OTP tab */
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  useEffect(() => {
    setChallengeId(null);
    setOtpSent(false);
    setOtpCode('');
    setSubmitError('');
  }, [formData.mobile]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateMobile = () => {
    if (!formData.mobile) {
      return 'الرجاء إدخال رقم الجوال';
    }
    if (!/^05\d{8}$/.test(formData.mobile)) {
      return 'رقم الجوال غير صالح. يجب أن يبدأ بـ 05 ويتكون من 10 أرقام';
    }
    return null;
  };

  const validatePasswordForm = () => {
    const newErrors = {};
    const mobileErr = validateMobile();
    if (mobileErr) newErrors.mobile = mobileErr;
    if (!formData.password) {
      newErrors.password = 'الرجاء إدخال كلمة المرور';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const finishLoginRedirect = async () => {
    trackEvent('login_success', {
      page: '/login',
      metadata: { source: loginTab === 'otp' ? 'otp' : 'form' },
    });
    clearLocalAuthFlags();
    await login();
    const returnUrl = searchParams.get('returnUrl');
    if (returnUrl && isSafeReturnUrl(returnUrl)) {
      window.location.href = returnUrl;
      return;
    }
    navigate('/home', { replace: true });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!validatePasswordForm()) return;

    setIsLoading(true);

    try {
      const res = await fetch(buildApiUrl(API_ENDPOINTS.LOGIN), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          mobile: formData.mobile,
          password: formData.password,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        if (data?.code === 'PASSWORD_LOGIN_DISABLED') {
          throw new Error(data.message || 'تسجيل الدخول بكلمة المرور معطّل. استخدم رمز التحقق.');
        }
        throw new Error(data.message || 'حدث خطأ أثناء تسجيل الدخول');
      }

      await finishLoginRedirect();
    } catch (err) {
      setSubmitError(err.message || 'حدث خطأ أثناء تسجيل الدخول. الرجاء المحاولة مرة أخرى.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async () => {
    setSubmitError('');
    const mobileErr = validateMobile();
    if (mobileErr) {
      setErrors({ mobile: mobileErr });
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(buildApiUrl(API_ENDPOINTS.LOGIN_OTP_SEND), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mobile: formData.mobile }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'تعذر إرسال رمز التحقق');
      }
      setChallengeId(data.challengeId || null);
      setOtpSent(true);
      setOtpCode('');
    } catch (err) {
      setSubmitError(err.message || 'تعذر إرسال رمز التحقق');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setSubmitError('');

    const mobileErr = validateMobile();
    if (mobileErr) {
      setErrors({ mobile: mobileErr });
      return;
    }
    if (!challengeId) {
      setSubmitError('يرجى إرسال رمز التحقق أولاً.');
      return;
    }
    if (!otpCode || otpCode.replace(/\D/g, '').length < 6) {
      setSubmitError('يرجى إدخال رمز التحقق (6 أرقام).');
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(buildApiUrl(API_ENDPOINTS.LOGIN_OTP_VERIFY), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          mobile: formData.mobile,
          challengeId,
          code: otpCode.replace(/\D/g, ''),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'رمز التحقق غير صحيح');
      }
      await finishLoginRedirect();
    } catch (err) {
      setSubmitError(err.message || 'رمز التحقق غير صحيح');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    setIsLoading(true);
    setSubmitError('');
    try {
      const res = await fetch(buildApiUrl(API_ENDPOINTS.AUTH_GUEST), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.user?.isGuest) {
        throw new Error(data?.message || 'تعذر بدء وضع الضيف');
      }
      enterGuestMode(data.user);
      navigate('/home');
    } catch (err) {
      setSubmitError(err.message || 'تعذر بدء وضع الضيف');
    } finally {
      setIsLoading(false);
    }
  };

  const showOtpTab = otpConfig.otpLoginEnabled;
  const showPasswordTab = otpConfig.passwordLoginEnabled;
  const showTabs = showOtpTab && showPasswordTab;
  const activeOtpView = showOtpTab && (loginTab === 'otp' || !showPasswordTab);
  const activePasswordView = showPasswordTab && !activeOtpView;

  return (
    <Layout>
      <div className={`auth-page ${isRTL ? 'rtl' : ''}`}>
        <div className="auth-card">
          <div className="auth-header">
            <h1 className="auth-title">تسجيل الدخول</h1>
            <p className="auth-subtitle">
              {activeOtpView
                ? otpSubtitle(otpConfig.deliveryHint)
                : 'أدخل بيانات حسابك للدخول إلى لوحة التحكم'}
            </p>
          </div>

          <div className="auth-form-container">
            {submitError && (
              <div className="auth-error-box">
                {submitError}
              </div>
            )}

            {showTabs ? (
              <div className="auth-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={loginTab === 'otp'}
                  className={`auth-tab ${loginTab === 'otp' ? 'auth-tab-active' : ''}`}
                  onClick={() => {
                    setLoginTab('otp');
                    setSubmitError('');
                  }}
                >
                  رمز التحقق
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={loginTab === 'password'}
                  className={`auth-tab ${loginTab === 'password' ? 'auth-tab-active' : ''}`}
                  onClick={() => {
                    setLoginTab('password');
                    setSubmitError('');
                  }}
                >
                  كلمة المرور
                </button>
              </div>
            ) : null}

            {activeOtpView ? (
              <form onSubmit={handleVerifyOtp} className="auth-form">
                <div className="auth-input-group">
                  <label htmlFor="mobile-otp" className="auth-label">
                    رقم الجوال *
                  </label>
                  <input
                    id="mobile-otp"
                    name="mobile"
                    type="tel"
                    className={`auth-input ${errors.mobile ? 'auth-input-error' : ''}`}
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="05xxxxxxxx"
                    disabled={isLoading}
                  />
                  {errors.mobile && <div className="auth-error">{errors.mobile}</div>}
                </div>

                {!otpSent ? (
                  <button
                    type="button"
                    className="auth-button"
                    disabled={isLoading}
                    onClick={handleSendOtp}
                  >
                    {isLoading ? 'جاري الإرسال...' : 'إرسال رمز التحقق'}
                  </button>
                ) : (
                  <>
                    <p className="auth-hint">
                      {otpSentHint(otpConfig.deliveryHint)}
                    </p>
                    <div className="auth-input-group">
                      <label htmlFor="otpCode" className="auth-label">
                        رمز التحقق (6 أرقام)
                      </label>
                      <input
                        id="otpCode"
                        name="otpCode"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        className="auth-input auth-input-otp"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        maxLength={6}
                        disabled={isLoading}
                      />
                    </div>
                    <button
                      type="submit"
                      className="auth-button"
                      disabled={isLoading || otpCode.length < 6}
                    >
                      {isLoading ? 'جاري التحقق...' : 'تأكيد وتسجيل الدخول'}
                    </button>
                    <button
                      type="button"
                      className="auth-button-secondary auth-button-inline"
                      disabled={isLoading}
                      onClick={handleSendOtp}
                    >
                      إعادة إرسال الرمز
                    </button>
                  </>
                )}
              </form>
            ) : activePasswordView ? (
              <form onSubmit={handlePasswordSubmit} className="auth-form">
                <div className="auth-input-group">
                  <label htmlFor="mobile" className="auth-label">
                    رقم الجوال *
                  </label>
                  <input
                    id="mobile"
                    name="mobile"
                    type="tel"
                    className={`auth-input ${errors.mobile ? 'auth-input-error' : ''}`}
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="05xxxxxxxx"
                  />
                  {errors.mobile && <div className="auth-error">{errors.mobile}</div>}
                </div>

                <div className="auth-input-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label htmlFor="password" className="auth-label">
                      كلمة المرور *
                    </label>
                    <Link to="/forgot-password" className="auth-link" style={{ fontSize: '0.875rem' }}>
                      نسيت كلمة المرور؟
                    </Link>
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    className={`auth-input ${errors.password ? 'auth-input-error' : ''}`}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                  />
                  {errors.password && (
                    <div className="auth-error">{errors.password}</div>
                  )}
                </div>

                <button
                  type="submit"
                  className="auth-button"
                  disabled={isLoading}
                >
                  {isLoading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
                </button>
              </form>
            ) : (
              <p className="auth-hint" role="status">
                تسجيل الدخول غير متاح حالياً. تواصل مع الدعم.
              </p>
            )}

            <div className="divider">
              <span>أو</span>
            </div>

            <button
              type="button"
              className="auth-button-secondary"
              onClick={handleGuestLogin}
              disabled={isLoading}
            >
              الدخول كضيف
            </button>

            <div className="auth-footer">
              <p className="auth-footer-text">
                ليس لديك حساب؟{' '}
                <Link to="/register" className="auth-link-button">
                  سجل الآن
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default LoginPage;
